import { ChartData } from 'chart.js';
import { generateDataset } from 'components/Chart/helpers';
import * as R from 'remeda';
import { createSelector } from 'reselect';
import Types from 'Types';
import { guardUtility, kpiUtility, timeUtility } from 'utility';

import { aggregatedActivitySelectors } from './aggregatedActivity';
import { calculatedInternalSelectors } from './calculatedInternal';
import { forecastSelectors } from './forecast';
import { internalHourSelectors } from './internalHour';

export const domain = (state: Types.RootState) =>
  state.aggregated.aggregatedActivity;

export const kpi = createSelector(
  aggregatedActivitySelectors.kpi,
  calculatedInternalSelectors.kpi,
  forecastSelectors.kpi,
  internalHourSelectors.kpi,
  (
    { hours, modifiedHours, movingHoursTotal },
    {
      noInternalBacklog,
      noInternalProcessed,
      noInternalReceived,
      target,
      movingNoInternalProcessedTotal,
    },
    { forecast, forecastHistory },
    { internalHourProccessed },
  ) => {
    const {
      actual,
      hoursFromGoal,
      forecastDeviation,
      movingAverage,
    }: KPI.AggregatedValues = {
      actual: kpiUtility.calculateProductivity(
        noInternalProcessed,
        modifiedHours,
      ),
      hoursFromGoal: kpiUtility.calculateHoursFromGoal(
        noInternalProcessed,
        modifiedHours,
        target,
      ),
      forecastDeviation: kpiUtility.calculateDeviation(
        forecast,
        noInternalProcessed,
      ),

      movingAverage: kpiUtility.calculateProductivity(
        movingNoInternalProcessedTotal,
        movingHoursTotal,
      ),
    };

    const kpis: KPI.Values = {
      hours,
      modifiedHours,
      movingHoursTotal,
      actual,
      forecastHistory,
      forecast,

      hoursFromGoal,
      noInternalBacklog,
      noInternalProcessed,
      noInternalReceived,
      movingNoInternalProcessedTotal,
      target,
      movingAverage,
      forecastDeviation,
      projectionBaseDeviation: 0,
      internalHourProccessed,
    };
    return kpis;
  },
);

// TODO: don't know how we will treat this, so no point in making it beautiful
// ! Only works for days
function lastYearDateIds(currentDateIds: string[]): string[] {
  const days = currentDateIds.length;
  const dateIds: string[] = [];

  // ? why is it times days, it gets weird quick?
  R.times(days, (index) => {
    if (index === 0) {
      const timeDate = timeUtility.dateFromId(currentDateIds[0] ?? '');
      if (!R.isDefined(timeDate)) return;
      if (timeDate.type !== 'day') return;
      let historicTimeDate = timeUtility.step({
        timeDate,
        step: 'previous',
        stepSize: 'year',
      });

      // 0 + 1 = 1
      if (historicTimeDate.dayOfWeek === 0) {
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'next',
          stepSize: 'day',
        });
      }
      // 2 - 1 = 1;
      else if (historicTimeDate.dayOfWeek === 2) {
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'previous',
          stepSize: 'day',
        });
      }
      // 3  - 2 = 1
      else if (historicTimeDate.dayOfWeek === 3) {
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'previous',
          stepSize: 'day',
        });
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'previous',
          stepSize: 'day',
        });
        // 4 - 3 = 1;
        // 4 + 3 = 1; .... hur ska jag tänka
      } else if (historicTimeDate.dayOfWeek === 4) {
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'previous',
          stepSize: 'day',
        });
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'previous',
          stepSize: 'day',
        });
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'previous',
          stepSize: 'day',
        });
      }
      // 5 + 2 = 1;
      else if (historicTimeDate.dayOfWeek === 5) {
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'next',
          stepSize: 'day',
        });
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'next',
          stepSize: 'day',
        });
        // 6 + 1 = 1
      } else if (historicTimeDate.dayOfWeek === 6) {
        historicTimeDate = timeUtility.step({
          timeDate: historicTimeDate,
          step: 'next',
          stepSize: 'day',
        });
      }

      dateIds.push(historicTimeDate.id);
    } else {
      const previousDateId = dateIds[index - 1];
      if (!R.isDefined(previousDateId)) return;

      const previousDay = timeUtility.dateFromId(previousDateId);

      if (!R.isDefined(previousDay)) return;
      if (previousDay.type !== 'day') return;

      const newDay = timeUtility.step({
        timeDate: previousDay,
        step: 'next',
        stepSize: 'day',
      });
      dateIds.push(newDay.id);
    }
  });
  return dateIds;
}

function yesterdayDateIds(currentDateIds: string[]) {
  return R.pipe(
    currentDateIds,
    R.map((currentDateId) => {
      const timeDate = timeUtility.dateFromId(currentDateId);
      if (!R.isDefined(timeDate)) return;
      if (timeDate.type === 'year')
        guardUtility.assertNotAllowed(timeDate.type);
      if (timeDate.type === 'month')
        guardUtility.assertNotAllowed(timeDate.type);
      if (timeDate.type === 'week')
        return guardUtility.assertNotAllowed(timeDate.type);

      const yesterday = timeUtility.step({
        timeDate,
        step: 'previous',
        stepSize: 'day',
      });

      return yesterday.id;
    }),
    R.filter(R.isDefined),
  );
}

/**
 * Idea is to call this one to get all relevant chartData
 * Rewrite a bit more to not render that often?
 */
export const chartData = createSelector(
  (
    state: Types.RootState,
    ownProps: { datasets: Charts.Dataset[]; dateIds: string[] },
  ) => {
    const chartData: ChartData = {
      datasets: [],
      labels: ownProps.dateIds.map((_dateId, index) => index + 1), // Necessary since scatter relies on labels are parsable to numbers
    };
    chartData.datasets = ownProps.datasets.map((dataset) => {
      let dateIds: string[] = [];
      if (dataset.historic === 'last-year') {
        dateIds = lastYearDateIds(ownProps.dateIds);
      } else if (dataset.historic === 'yesterday') {
        dateIds = yesterdayDateIds(ownProps.dateIds);
      } else {
        dateIds = ownProps.dateIds;
      }

      const data = dateIds.map((dateId) => {
        const kpiValue = kpi(state, {
          dateId,
          entityIds: dataset.entityIds,
          filterEntityIds: dataset.filterEntityIds,
        })[dataset.kpi];

        return kpiValue;
      });

      return generateDataset(dataset, data);
    });

    return chartData;
  },
  (chartData) => {
    return chartData;
  },
);
